import { PropertyListingStatus } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { FilterSelections } from '@unreserved-frontend-v2/ui/schema-table/models'
import { ValueHash } from '@unreserved-frontend-v2/utils/types'
import { ParsedUrlQuery } from 'querystring'
import { DEFAULT_COUNTRY_SLUG, ROUTE_BASED_PARAMS } from './constants'
import { getQueryParamForBounds, getQueryParamForGeoInput } from './object-filter-utils'
import { formatAndSanatizeSluggedFilters } from './parse-format-listings-slugged-values'
import { getQueryParamForSortInput, shouldRemoveFromQueryString, SortValue } from './sort-helpers'

const IGNORED_FILTER_KEYS = ['listingTypes', 'liked', 'inquired', 'listingStatuses', 'nextInternalLocale']
const IGNORED_FILTER_VALUES: unknown[] = [undefined, null, 'ALL', '']
const IGNORED_NON_FILTER_QUERY_PARAMS: ValueHash<string> = { listing: 'will-be-ignored' }

/**
 * Builds a query string for the current set of filter selections.
 * @param selections The current set of filter selections.
 * @param pathname The NextJS pathname for this route (e.g. - /real-estate/[listing])
 * @param noSlug True if the query contains a slug. False otherwise.
 * @param existingQuery Passed in if we need to keep track of any non-filter query params (e.g. - zoom).
 * @returns string Query string representation of the current filter values.
 */
export const buildQueryStringFromFilters = (
  selections: FilterSelections,
  pathname: string,
  noSlug?: boolean,
  existingQuery?: ParsedUrlQuery
): string => {
  // Separating slug recognized values
  const { sluggedFiltersString, queryStringFilters } = !noSlug
    ? formatAndSanatizeSluggedFilters(selections)
    : { queryStringFilters: selections, sluggedFiltersString: '' }

  // If an existing query value is passed in, keep track of all the query params that are not filter params
  // and then add them back to the URL once we're done calculating the values for the current filter selections.
  const nonFilterQueryParams: ValueHash<string> = {}
  if (existingQuery) {
    const filterQueryKeys = Object.keys(queryStringFilters)
    Object.keys(existingQuery).forEach((existingKey: string) => {
      if (!filterQueryKeys.includes(existingKey) && !IGNORED_NON_FILTER_QUERY_PARAMS[existingKey]) {
        nonFilterQueryParams[existingKey] = existingQuery[existingKey] as string
      }
    })
  }

  // Router based variables and their assigned values to be iterated on
  const routerBasedVariables = {
    [ROUTE_BASED_PARAMS.LISTING]: sluggedFiltersString,
  }

  // Assigning router-recognized variables to the variable in flattened pathname
  const flattenedPathname = Object.entries(routerBasedVariables).reduce(
    (acc, [key, value]) => acc.replace(`[${key}]`, value),
    pathname
  )

  const queryStringParams = Object.keys(queryStringFilters).reduce((newUrl: string, filterKey: string) => {
    const rawFilterValue = queryStringFilters[filterKey]
    const isFilterValueObject = !!rawFilterValue && typeof rawFilterValue === 'object'
    const filterValue =
      !isFilterValueObject || Object.keys(rawFilterValue as object).length ? rawFilterValue : undefined

    // Return the current newURL for any of these conditions because we won't handle that filter key/value.
    if (
      IGNORED_FILTER_KEYS.includes(filterKey) ||
      IGNORED_FILTER_VALUES.includes(filterValue) ||
      /**
       * Need to encode this in the URL when we are on a path that does not have the slug encoded at the beginning
       * of the URL (e.g. - http://localhost:4200/real-estate/liked?placeSlugs=toronto-on)
       */
      ((filterValue as string[]).includes?.(DEFAULT_COUNTRY_SLUG) && filterKey === 'placeSlugs') ||
      /**
       * When geo is cleared from the filters, we need to also remove associated sort values from the queryString
       * and when the default sort (NewToOld) is selected, hide it from the URL's to keep them clean.
       */
      (!isFilterValueObject && shouldRemoveFromQueryString(filterKey, filterValue as string, queryStringFilters['geo']))
    ) {
      return newUrl
    }

    if (filterKey === 'boundsRestricted') {
      // Only put boundsRestricted in the URL if it's disabled (it's enabled by default).
      return filterValue ? newUrl : `${newUrl}${newUrl === '' ? '' : '&'}boundsRestricted=false`
    }

    if (newUrl !== '') {
      newUrl += '&'
    }

    if (filterKey === 'geo') {
      return (newUrl += getQueryParamForGeoInput(filterValue))
    }

    if (filterKey === 'bounds') {
      return (newUrl += getQueryParamForBounds(filterValue))
    }

    if (filterKey === 'sort') {
      return (newUrl += getQueryParamForSortInput(
        filterValue as SortValue,
        selections['listingStatuses'] as PropertyListingStatus
      ))
    }

    const defaultValue = typeof filterValue === 'object' ? encodeURIComponent(JSON.stringify(filterValue)) : filterValue

    return (newUrl += `${filterKey}=${defaultValue}`)
  }, '')

  let newQueryString = queryStringParams ? `${flattenedPathname}?${queryStringParams}` : flattenedPathname

  // Add the existing, non-filter query params back to the URL.
  const nonFilterKeys = Object.keys(nonFilterQueryParams)
  if (nonFilterKeys.length) {
    if (!queryStringParams) {
      newQueryString += '?'
    }

    nonFilterKeys.forEach((k, index) => {
      newQueryString += `${index !== 0 || queryStringParams ? '&' : ''}${k}=${nonFilterQueryParams[k]}`
    })
  }

  return newQueryString
}
